import { Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ActionCompletedHouseholds, SplitHouseholds, findLeader } from '../../../helpers/households';
import { TrackingContext } from '../../../TrackingContext';
import { messagingHubRoute } from '../../../helpers/routes';
import FullScreenModal from '../../Modals/FullScreenModal';
import FullScreenHeader from '../../Modals/FullScreenModal/Header';
import AddGuestRail, { Household } from '../AddGuestRail';
import { cleanSetEditHousehold, sanitizeCollectionForGDS } from '../helper';
import HouseholdRail from '../HouseholdRail';
import ReCaptcha from '../ReCaptcha';
import SendConfirmation from '../SendConfirmation';
import styles from '../share.scss';
import { SetFilterCriteria } from '../../MessagingHub/GuestFilters/useFilteredHouseholds';
import { FilterPresetState } from '../../MessagingHub/GuestFilters';

export interface ShareValues {
  subject: string;
  message: string;
  linkButtonText: string;
}

export interface EmailSequenceProps {
  initialValues: any;
  onSend: (values: any) => void;
  PreviewComponent: React.ReactNode;
  EditorComponent: React.ReactNode;
  // Only used for passing custom household rail
  HouseholdRailComponent?: React.ComponentType<any>;
  households: SplitHouseholds | ActionCompletedHouseholds;
  showSuccess: boolean;
  showError: boolean;
  errorMessage: string | undefined;
  loading: boolean;
  hideRecaptcha: boolean;
  setHideRecaptcha: (val: boolean) => void;
  setGuestIds: (ids: string[]) => void;
  validationSchema: any;
  // used for route matching
  editPath: string;
  // used in actual navigation
  editRoute: string;
  recaptchaRef: React.RefObject<{
    reset: () => void;
    getValue: () => string;
  }>;
  householdLoading: boolean;
  setFilter: SetFilterCriteria;
  filterPreset?: FilterPresetState;
  displayName: string | undefined;
}

const EmailSequence: React.FC<EmailSequenceProps> = ({
  PreviewComponent,
  HouseholdRailComponent = HouseholdRail,
  households,
  initialValues,
  loading,
  onSend,
  showError,
  errorMessage,
  showSuccess,
  hideRecaptcha,
  setHideRecaptcha,
  recaptchaRef,
  setGuestIds,
  editRoute,
  validationSchema,
  householdLoading,
  setFilter,
  filterPreset,
  displayName,
}) => {
  const history = useHistory();
  const [guestRailOpen, setGuestRailOpen] = useState(false);
  const [source, setSource] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedHouseholds, setSelectedHouseholds] = useState<Set<string>>(new Set());
  const [editHousehold, setEditHousehold] = useState<Household>();
  const [householdsList, setHouseholdsList] = useState<Household[]>();
  const { setPagesLoaded } = useContext(TrackingContext);

  const queryParams = new URLSearchParams(window.location.search);
  const composeSource = queryParams.get('source');
  if (composeSource && composeSource !== source) {
    setSource(composeSource);
  }
  useEffect(() => {
    const ids = new Set<string>();

    households.contactable.forEach(h => {
      const leader = findLeader(h)!;
      ids.add(leader.id);
    });

    setSelectedHouseholds(ids);
  }, [households, setSelectedHouseholds]);
  useEffect(() => {
    setPagesLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (showError || showSuccess) {
      setIsSubmitting(false);
    }
  }, [showError, showSuccess]);

  // Add timeout to prevent stuck loading states
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (isSubmitting && !showSuccess && !showError) {
      timeout = setTimeout(() => {
        setIsSubmitting(false);
      }, 30000); // 30-second timeout
    }
    return () => clearTimeout(timeout);
  }, [isSubmitting, showSuccess, showError]);

  // Take user to where they came from
  const backHandler = () => {
    let pathname = window.location.pathname.replace('/gs/dashboard', '');
    if (pathname.indexOf('/edit') !== -1) {
      exitSequenceHandler();
    } else {
      history.push(messagingHubRoute);
    }
  };
  const exitSequenceHandler = () => {
    if (source) {
      window.location.replace(source);
    } else {
      history.push(messagingHubRoute);
    }
  };

  const showRecaptcha = (ids: string[]) => {
    setGuestIds(ids);
    setHideRecaptcha(false);
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} onSubmit={onSend} validationSchema={validationSchema}>
      {({ submitForm }) => (
        <FullScreenModal>
          <div className={styles.shareContainer} data-testid="share-page">
            <div className={styles.sequenceHeader}>
              <FullScreenHeader backText="Back" onBack={backHandler} />
            </div>
            <HouseholdRailComponent
              sendMessage={showRecaptcha}
              showSpinner={loading}
              showSuccess={showSuccess}
              showError={showError}
              errorMessage={errorMessage}
              onHideSuccess={exitSequenceHandler}
              setGuestRailOpen={setGuestRailOpen}
              setEditHousehold={(household: Household) => cleanSetEditHousehold(household, setEditHousehold)}
              setHouseholdsList={(households: Household[]) =>
                setHouseholdsList(() => (households?.length ? sanitizeCollectionForGDS(households) : undefined))
              }
              editRoute={editRoute}
              households={households}
              householdRailType="email"
              householdLoading={householdLoading}
              selectedHouseholds={selectedHouseholds}
              setSelectedHouseholds={setSelectedHouseholds}
              RecaptchaComponent={
                <ReCaptcha
                  recaptchaRef={recaptchaRef}
                  submitForm={submitForm}
                  hideRecaptcha={hideRecaptcha}
                  setHideRecaptcha={setHideRecaptcha}
                  setIsSubmitting={setIsSubmitting}
                />
              }
              SuccessComponent={
                <SendConfirmation showSuccess={showSuccess} showError={showError} isSubmitting={isSubmitting} />
              }
              setFilter={setFilter}
              filterPreset={filterPreset}
              messageType={displayName}
              messageMedium="email"
            />
            <AddGuestRail
              editHousehold={editHousehold}
              setEditHousehold={setEditHousehold}
              setHouseholdsList={setHouseholdsList}
              guestRailOpen={guestRailOpen}
              setGuestRailOpen={setGuestRailOpen}
              sequence={'email'}
              selectedHouseholds={selectedHouseholds}
              setSelectedHouseholds={setSelectedHouseholds}
              householdsList={householdsList}
              messageType={displayName}
              messageMedium="email"
            />
            {PreviewComponent}
          </div>
        </FullScreenModal>
      )}
    </Formik>
  );
};

export default EmailSequence;
